import { useEffect, type ReactElement, type ReactNode } from 'react';
import { type ApiClient } from '../api/client';
import { authStore } from '../auth/auth-store';
import { featureFlagsStore, featureKey, type FeatureFlagsState } from './feature-flags-store';

/**
 * `useFeatureFlag('yard')` / `useFeatureFlag('feature.yard')`. Overrides win over server flags,
 * then runtime-config `features`; `fallback` applies only when none of them knows the key.
 */
export function useFeatureFlag(key: string, fallback = false): boolean {
  const full = featureKey(key);
  const override = featureFlagsStore((s) => s.overrides[full]);
  const flag = featureFlagsStore((s) => s.flags[full]);
  if (override !== undefined) return override;
  if (flag !== undefined) return flag;
  return featureFlagsStore.getState().isEnabled(full) || fallback;
}

export function useFeatureFlags(): FeatureFlagsState {
  return featureFlagsStore();
}

export interface FeatureFlagProviderProps {
  children: ReactNode;
  client?: ApiClient;
  /** Load flags once the user is authenticated (default true). */
  autoLoad?: boolean;
}

/** Loads `feature.*` effective configuration when the session is authenticated. */
export function FeatureFlagProvider({ children, client, autoLoad = true }: FeatureFlagProviderProps): ReactElement {
  const authStatus = authStore((s) => s.status);
  useEffect(() => {
    if (!autoLoad || authStatus !== 'authenticated') return;
    const state = featureFlagsStore.getState();
    if (state.status === 'idle' || state.status === 'error') void state.load(client);
  }, [authStatus, autoLoad, client]);
  return <>{children}</>;
}
